import { DsIconButton } from '@frontends/ds';
import * as htmlToImage from 'html-to-image';
import { forwardRef, useEffect, useRef, useState } from 'react';
import i18n from '../../../i18n';

const IMAGE_GENERATOR_MIN_PIXEL_RATIO = 2;
const IMAGE_GENERATOR_MAX_PIXEL_RATIO = 3;

function ActionDownload({ onClick, className }: { onClick: () => void; className?: string }): React.JSX.Element {
  return (
    <DsIconButton
      variant="secondary"
      size="sm"
      name="download"
      aria-label={i18n.t('sessionStatistic.modal.actions.download')}
      onPress={onClick}
      className={className}
    />
  );
}

function ActionOpen({ onClick, className }: { onClick: () => void; className?: string }): React.JSX.Element {
  return (
    <DsIconButton
      variant="secondary"
      size="sm"
      name="external-link"
      aria-label={i18n.t('sessionStatistic.modal.actions.open')}
      onPress={onClick}
      className={className}
    />
  );
}

function ActionShare({ onClick, className }: { onClick: () => void; className?: string }): React.JSX.Element {
  return (
    <DsIconButton
      variant="secondary"
      size="sm"
      name="share-2"
      aria-label={i18n.t('sessionStatistic.modal.actions.share')}
      onPress={onClick}
      className={className}
    />
  );
}

async function imageGeneratorDownload(ref: React.RefObject<HTMLDivElement | null>): Promise<void> {
  const dataUrl = await imageGeneratorHandler(ref);
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = `${i18n.t('panel.title')}.png`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

async function imageGeneratorOpenInNewWindow(ref: React.RefObject<HTMLDivElement | null>): Promise<void> {
  const dataUrl = await imageGeneratorHandler(ref);
  const newWindow = window.open('', '_blank');

  if (newWindow) {
    const img = document.createElement('img');
    img.src = dataUrl;
    img.alt = i18n.t('panel.title');
    img.style.width = `100%`;
    img.style.height = `100%`;
    img.style.objectFit = 'contain';
    newWindow.document.body.appendChild(img);
  }
}

async function imageGeneratorShare(
  ref: React.RefObject<HTMLDivElement | null>,
): Promise<{ success: boolean; method: string }> {
  const dataUrl = await imageGeneratorHandler(ref);
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  const file = new File([blob], `${i18n.t('panel.title')}.png`, { type: 'image/png' });

  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({
        files: [file],
      });

      return { success: true, method: 'native' };
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        return { success: false, method: 'native' };
      }
    }
  }

  if (navigator.clipboard && typeof ClipboardItem !== 'undefined') {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'image/png': blob,
        }),
      ]);

      return { success: true, method: 'clipboard' };
    } catch {}
  }

  await imageGeneratorDownload(ref);

  return { success: true, method: 'download' };
}

async function ensureAllImagesLoaded(el: HTMLElement): Promise<void> {
  try {
    await document.fonts?.ready;
  } catch {}

  const imgs = Array.from(el.querySelectorAll('img[data-share-exclude]')) as HTMLImageElement[];

  if (imgs.length === 0) {
    return;
  }

  await Promise.all(imgs.map((img) => waitForImageLoad(img)));

  await new Promise((resolve) => setTimeout(resolve, 200));

  try {
    await document.fonts?.ready;
  } catch {}
}

async function waitForImageLoad(img: HTMLImageElement): Promise<void> {
  if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    const timeout = setTimeout(() => {
      cleanup();
      resolve();
    }, 10000);

    const cleanup = (): void => {
      clearTimeout(timeout);
      img.removeEventListener('load', handleLoad);
      img.removeEventListener('error', handleError);
    };

    const handleLoad = (): void => {
      cleanup();
      resolve();
    };

    const handleError = (): void => {
      cleanup();
      resolve();
    };

    img.addEventListener('load', handleLoad);
    img.addEventListener('error', handleError);

    if (typeof img.decode === 'function') {
      img
        .decode()
        .then(() => {
          if (img.complete && img.naturalWidth > 0) {
            cleanup();
            resolve();
          }
        })
        .catch(() => undefined);
    }
  });
}

async function imageGeneratorHandler(ref: React.RefObject<HTMLDivElement | null>): Promise<string> {
  const el = ref.current;

  if (!el) {
    throw new Error('element not found');
  }

  await ensureAllImagesLoaded(el);

  const rect = el.getBoundingClientRect();
  const width = Math.max(1, Math.ceil(rect.width));
  const height = Math.max(1, Math.ceil(rect.height));
  const pixelRatio = Math.min(
    Math.max(window.devicePixelRatio || 1, IMAGE_GENERATOR_MIN_PIXEL_RATIO),
    IMAGE_GENERATOR_MAX_PIXEL_RATIO,
  );

  const imageOptions = {
    cacheBust: true,
    pixelRatio,
    width,
    height,
    canvasWidth: width,
    canvasHeight: height,
    style: {
      width: `${width}px`,
      height: `${height}px`,
    },
    filter: (node: HTMLElement) => !node.dataset?.shareExclude,
    skipFonts: true,
  };

  try {
    await htmlToImage.toBlob(el, imageOptions);
    await new Promise((resolve) => setTimeout(resolve, 100));
  } catch {}

  try {
    const blob = await htmlToImage.toBlob(el, imageOptions);

    if (blob) {
      const url = URL.createObjectURL(blob);

      return url;
    }
  } catch (error) {
    console.warn('Failed to generate image via toBlob, trying toPng:', error);
  }

  try {
    const dataUrl = await htmlToImage.toPng(el, imageOptions);

    return dataUrl;
  } catch (error) {
    console.error('Failed to generate image:', error);
    throw new Error('Failed to generate image', { cause: error });
  }
}

export const ShareSessionStatistic = forwardRef<HTMLDivElement | null, Record<never, never>>((_props, ref) => {
  const [copiedTooltipVisible, setCopiedTooltipVisible] = useState(false);
  const tooltipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  const handleDownload = async (): Promise<void> => {
    await imageGeneratorDownload(ref as React.RefObject<HTMLDivElement | null>);
  };
  const handleOpenInNewWindow = async (): Promise<void> => {
    await imageGeneratorOpenInNewWindow(ref as React.RefObject<HTMLDivElement | null>);
  };
  const handleShare = async (): Promise<void> => {
    const result = await imageGeneratorShare(ref as React.RefObject<HTMLDivElement | null>);

    if (result?.method === 'clipboard' && result.success) {
      setCopiedTooltipVisible(true);

      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }

      tooltipTimeoutRef.current = setTimeout(() => {
        setCopiedTooltipVisible(false);
      }, 2500);
    }
  };

  return (
    <div className="flex justify-center gap-3">
      <ActionDownload onClick={async () => await handleDownload()} />
      <ActionOpen onClick={async () => await handleOpenInNewWindow()} />
      <div className="relative">
        <ActionShare onClick={async () => await handleShare()} />
        {copiedTooltipVisible && (
          <div
            className="absolute bottom-full left-1/2 mb-2 -translate-x-1/2 whitespace-nowrap rounded-md bg-backgroundInverse px-2 py-1 text-sm text-textOnInverse shadow"
            role="status"
          >
            {i18n.t('sessionStatistic.modal.actions.copied')}
          </div>
        )}
      </div>
    </div>
  );
});

ShareSessionStatistic.displayName = 'ShareSessionStatistic';
